import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationStart, Router, Event as RouterEventUnion } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { StorageKeys } from '@app/constants';
import { FeedbackService, ReportService } from '@app/core/services';
import { BaseListSettings } from '@app/interfaces';
import { SortOrder, TableConfig } from '@app/interfaces/table';
import { BaseEntity } from '@app/models';
import { IListService } from '@app/services/IListService';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { TableComponent } from '@app/shared/components/table/table.component';
import { localStorage } from '@app/utils/storage';
import { singularToPlural } from '@app/utils/transforms';
import { BaseFilterComponent } from './base-filter.component';

@Component({
	template: '',
})
export abstract class BaseListComponent<T extends BaseEntity, T2 extends IListService>
	extends BaseFilterComponent<T>
	implements OnInit, OnDestroy, AfterViewInit
{
	@ViewChild(TableComponent) table: TableComponent;

	disableSavedSettings = false;

	data: T[] = [];
	primaryKey: keyof T = 'Id';
	config: TableConfig = {
		columns: [],
		actions: [],
		onRowClick: (row: any): void => this.onRowClicked(row),
		bulkActions: [],
		defaultSortDirection: 'asc',
		defaultSortField: null,
		showRowActions: true,
		showRowSelect: false,
	};
	ignoreGetList: boolean;
	/**
	 * If the data comes back as part of a complex object, specify the name of the property that contains the array of list objects
	 */
	listArrayPropertyName: string = null;
	exportFilename = 'Data.csv';

	protected readonly data$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
	protected readonly destroy$ = new Subject();
	protected readonly listService: T2;

	abstract entityName: string;
	abstract listingPagePath: string;

	constructor(
		protected readonly dialog: MatDialog,
		protected readonly feedbackService: FeedbackService,
		protected readonly route: ActivatedRoute,
		protected readonly router: Router,
		protected readonly reportService: ReportService
	) {
		super();
	}

	@HostListener('window:beforeunload') onBeforeUnload(): void {
		localStorage.remove(StorageKeys.BaseListSettings);
	}

	ngOnInit(): void {
		this.getList();
		this.afterOnInit();
	}

	ngOnDestroy(): void {
		this.beforeOnDestroy();
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	ngAfterViewInit(): void {
		if (!this.disableSavedSettings) {
			this.restoreSettings();
			this.router.events
				.pipe(
					filter((event: RouterEventUnion): event is NavigationStart => event instanceof NavigationStart),
					takeUntil(this.destroy$)
				)
				.subscribe((event: NavigationStart) => {
					this.persistSettings(event.url);
				});
		}
	}

	restoreSettings(): void {
		const persistedSettings = this.getPersistedSettings;
		if (persistedSettings === null) {
			return;
		}

		const persistedSetting = persistedSettings;
		const url = this.router.url;
		const from: string = persistedSetting.child || '';
		const fromChildToParent = from.toLowerCase().includes(url.toLowerCase()) && from.length > url.length;
		if (fromChildToParent) {
			const { filters, pageIndex, pageSize, sort } = persistedSetting;
			setTimeout(() => {
				this.table?.updatePageIndex(pageIndex);
				this.table?.updateSort(sort);
				this.table?.updatePageSize(pageSize);
				this.filter?.initialize(filters.search);
				this.applyFilter(filters);
			}, 0);
		}
	}

	persistSettings(nextUrl: string): void {
		const current = this.router.url;

		const fromParentToChild = nextUrl.toLowerCase().includes(current.toLowerCase()) && nextUrl.length > current.length;
		const name = this.config.name || this.constructor.name;

		localStorage.remove([StorageKeys.BaseListSettings, name]);

		if (fromParentToChild && this.table) {
			const sort = {
				direction: this.table.matSort.direction as SortOrder,
				active: this.table.matSort.active,
			};
			const settings: BaseListSettings = {
				[name]: {
					filters: this.filters,
					pageIndex: this.table.pageIndex,
					pageSize: this.table.pageSize,
					sort,

					parent: current,
					child: nextUrl,
				},
			};
			const persistedSettings = localStorage.get<BaseListSettings>(StorageKeys.BaseListSettings);

			const updatedSettings = { ...persistedSettings, ...settings};

			localStorage.set(StorageKeys.BaseListSettings, updatedSettings);
		}
	}

	get getPersistedSettings(): any {
		const persistedSettings = localStorage.get<BaseListSettings>(StorageKeys.BaseListSettings);
		const name = this.config.name || this.constructor.name;
		if (!persistedSettings?.[name]) {
			return null;
		}
		return persistedSettings[name];
	}

	// eslint-disable-next-line
	canDelete(id: any): boolean {
		//to be overridden by subclasses where needed
		return true;
	}

	deleteEntity(id: number | string): void {
		if (!this.canDelete(id)) {
			return;
		}

		this.dialog
			.open(ModalComponent, {
				maxWidth: '800px',
				data: {
					title: 'Confirm Delete',
					message: `Are you sure you want to delete this ${this.entityName}?`,
				},
			})
			.afterClosed()
			.subscribe((dialogResult) => {
				if (dialogResult) {
					this.listService.deleteEntity(id).subscribe((response) => {
						if (response && !response.isValid) {
							const errorMessage = response.errors;
							this.feedbackService.error(`There was a problem deleting ${this.entityName}: ${errorMessage}`);
							return;
						}
						this.refresh();
						this.feedbackService.success('deleted', this.entityName);
					});
				}
			});
	}

	refresh(): void {
		this.getList();
	}

	duplicate(entity: T): void {
		this.router.navigate(['./', 0], {
			relativeTo: this.route,
			queryParams: { openFlyout: 1, sections: ['details'], cancelRedirect: [this.listingPagePath] },
			state: { entity: { ...entity, [this.primaryKey]: 0 } },
		});
	}

	onRowClicked(entity: T): void {
		this.router.navigate(['./', entity[this.primaryKey]], { relativeTo: this.route });
	}

	bulkDelete(selection: any[]): void {
		if (!this.listService.bulkDeleteEntities) {
			return;
		}
		const confirmationText =
			selection.length === 1
				? `Are you sure you want to delete this ${this.entityName}?`
				: `Are you sure you want to delete these ${selection.length} ${singularToPlural(this.entityName)}?`;
		const pluralText = singularToPlural(this.entityName);
		this.dialog
			.open(ModalComponent, {
				maxWidth: '800px',
				data: {
					title: 'Confirm Delete',
					message: confirmationText,
				},
			})
			.afterClosed()
			.subscribe((dialogResult) => {
				if (dialogResult) {
					this.listService.bulkDeleteEntities(selection.map((p) => p[this.primaryKey])).subscribe((data: any) => {
						if (data && !data.isValid) {
							this.feedbackService.error(`There was a problem deleting ${this.entityName}: ${data.errors}`);
							this.refresh();
							return;
						}

						this.refresh();
						if (selection.length > 1) {
							this.feedbackService.success('deleted', pluralText, { isPlural: true });
							return;
						}
						this.feedbackService.success('deleted', this.entityName);
					});
				}
			});
	}

	exportList(selection?: T[]): void {
		const columns = this.config.columns.filter((h) => typeof h.header === 'string');
		const reportData: Array<any>[] = [];

		const list = selection ?? this.data;

		list.forEach((row) => {
			const rowData: any[] = [];
			columns.forEach((column) => {
				let cellValue: any = row[column.key as keyof T];
				if (column.transform) {
					cellValue = column.transform({ value: cellValue, key: column.key, row });
				}
				rowData.push(cellValue);
			});
			reportData.push(rowData);
		});

		this.reportService.downloadCsv(
			columns.length,
			columns.map((h) => h.header as string),
			reportData,
			this.exportFilename
		);
	}

	/** ngOnInit cannot be used by a subclass because it would overwrite the base class's method.**/
	protected afterOnInit(): void {
		/* This method can be overridden by the sub class and is called at the end of the base class's ngOnInit */
	}

	/** ngOnDestroy cannot be used by a subclass because it would overwrite the base class's method.**/
	protected beforeOnDestroy(): void {
		/* This method can be overridden by the sub class and is called at the beginning of the base class's ngOnDestroy */
	}

	protected getList(): void {
		if (this.ignoreGetList === true) {
			return;
		}
		const listObservable = this.serverFilters.length > 0 ? this.listService.getListFiltered(this.serverFilters) : this.listService.getList();
		listObservable.subscribe((data: any) => {
			if (data.isValid === false) {
				this.feedbackService.error(data.errors);
				return;
			}

			this.data = (this.listArrayPropertyName ? data[this.listArrayPropertyName] : data) as T[];
			this.data$.next(this.data);
		});
	}
}
